import React from "react";
import SiteHeader from "./SiteHeader";
import Reveal from "./Reveal";
import { IconArrowRight } from "./Icons";
import { LEGAL_LAST_UPDATED } from "../siteConfig";
import { useDocumentMeta } from "../useDocumentMeta";
import "../styles/legal.css";

const RELATED = [
  { label: "Privacy Statement", href: "/privacy" },
  { label: "Terms of Service", href: "/terms" },
  { label: "Cookies Notice", href: "/cookies" },
  { label: "Security", href: "/security" },
  { label: "Subprocessors", href: "/subprocessors" },
  { label: "Privacy Requests", href: "/privacy-requests" },
];

type LegalLayoutProps = {
  /** Page heading, e.g. "Privacy Statement". */
  title: string;
  /** Meta description for search and social cards. */
  description: string;
  /** Route path, e.g. "/privacy" — used for canonical and related-link state. */
  path: string;
  /** One-line summary shown under the title. */
  intro?: string;
  children: React.ReactNode;
};

/**
 * Shared shell for every legal / policy page.
 *
 * Previously each of these pages duplicated its own header, container, and
 * last-updated stamp. Centralising it means the policy date and cross-links
 * can never drift between documents.
 */
const LegalLayout: React.FC<LegalLayoutProps> = ({
  title,
  description,
  path,
  intro,
  children,
}) => {
  useDocumentMeta({
    title: `${title} | PABS`,
    description,
    canonicalPath: path,
    ogImage: "https://pabs.online/logo512.png",
  });

  const related = RELATED.filter((r) => r.href !== path);

  return (
    <>
      <SiteHeader />

      <main id="main" className="lg">
        <section className="lg__hero">
          <div className="lg__bg" aria-hidden="true" />
          <div className="u-container">
            <Reveal className="lg__head">
              <span className="t-eyebrow">Legal</span>
              <h1 className="t-h1 lg__title">{title}</h1>
              {intro ? <p className="t-lead lg__intro">{intro}</p> : null}
              <p className="lg__meta">Last updated: {LEGAL_LAST_UPDATED}</p>
            </Reveal>
          </div>
        </section>

        <div className="u-container lg__layout">
          <article className="lg__doc">{children}</article>

          <aside className="lg__aside" aria-label="Related pages">
            <div className="lg__aside-card">
              <h2 className="lg__aside-title">Related pages</h2>
              <ul className="lg__aside-links">
                {related.map((r) => (
                  <li key={r.href}>
                    <a href={r.href}>
                      {r.label}
                      <IconArrowRight size={15} />
                    </a>
                  </li>
                ))}
              </ul>
            </div>

            <div className="lg__aside-help">
              <p>Questions about this policy?</p>
              <a className="btn btn--secondary btn--sm btn--block" href="/contact">
                Contact us
              </a>
            </div>
          </aside>
        </div>
      </main>
    </>
  );
};

export default LegalLayout;
